import React from 'react';
import { useTranslation } from 'react-i18next';
import GenericTablePage from '../components/GenericTablePage';
import { GridColDef } from '@mui/x-data-grid';
import { Tooltip, Chip } from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import { formatDate, formatDateTime } from '../lib/dateUtils';
import { enumChip } from '../lib/enumLabels';

const TendersPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : 'en';

  const columns: GridColDef[] = [
    {
      field: 'attachment',
      headerName: t('tenders.attachment'),
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={t('tenders.attachment')}>
            <AttachFile fontSize="small" color="action" />
          </Tooltip>
        ) : null,
    },
    { field: 'tender_name', headerName: t('tenders.tenderName'), flex: 1, minWidth: 250 },
    { field: 'reference_no', headerName: t('tenders.referenceNo'), width: 160 },
    { field: 'authority_dept', headerName: t('tenders.authorityDept'), width: 200 },
    { field: 'owner_name', headerName: t('tenders.ownerName'), width: 180 },
    {
      field: 'type',
      headerName: t('tenders.type'),
      width: 130,
      renderCell: (params) => {
        const chip = enumChip('type', params.value, locale);
        return chip ? <Chip label={chip.label} color={chip.color} size="small" /> : null;
      },
    },
    {
      field: 'is_public',
      headerName: t('tenders.isPublic'),
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common.yes') : t('common.no')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'needs_review',
      headerName: t('tenders.needsReview'),
      width: 130,
      sortable: false,
      renderCell: (params) =>
        params.value ? <Chip label={t('tenders.needsReview')} color="warning" size="small" /> : null,
    },
    {
      field: 'submission_deadline',
      headerName: t('tenders.submissionDeadline'),
      width: 150,
      valueFormatter: (value) => formatDate(value, locale, 'medium'),
    },
    {
      field: 'date',
      headerName: t('tenders.date'),
      width: 150,
      valueFormatter: (value) => formatDate(value, locale, 'medium'),
    },
    {
      field: 'created_at',
      headerName: t('tenders.createdAt'),
      width: 150,
      valueFormatter: (value) => formatDateTime(value, locale),
    },
  ];

  const fields = [
    { 
      name: 'tender_name', 
      label: t('tenders.tenderName'), 
      type: 'text' as const, 
      required: true,
      width: 'full' as const
    },
    {
      name: 'reference_no',
      label: t('tenders.referenceNo'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'authority_dept',
      label: t('tenders.authorityDept'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'tender_kind',
      label: t('tenders.tenderKind'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'owner_name',
      label: t('tenders.ownerName'),
      type: 'text' as const,
      required: true,
      width: 'third' as const
    },
    {
      name: 'financier_name',
      label: t('tenders.financierName'),
      type: 'text' as const,
      width: 'third' as const
    },
    { 
      name: 'area', 
      label: t('tenders.area'), 
      type: 'text' as const,
      width: 'third' as const
    },
    { 
      name: 'email', 
      label: t('tenders.email'), 
      type: 'email' as const,
      width: 'third' as const
    },
    { 
      name: 'phone_number', 
      label: t('tenders.phone'), 
      type: 'text' as const,
      width: 'third' as const
    },
    { 
      name: 'telephone_number', 
      label: t('tenders.telephone'), 
      type: 'text' as const,
      width: 'third' as const
    },
    { 
      name: 'type', 
      label: t('tenders.type'), 
      type: 'select' as const,
      width: 'third' as const,
      options: [
        { value: 'public', label: t('tenders.public') },
        { value: 'private', label: t('tenders.private') },
        { value: 'international', label: t('tenders.international') }
      ]
    },
    {
      name: 'insurance_value',
      label: t('tenders.insuranceValue'), 
      type: 'number' as const,
      width: 'third' as const
    },
    { 
      name: 'currency', 
      label: t('tenders.currency'), 
      type: 'currency' as const,
      width: 'third' as const
    },
    {
      name: 'link',
      label: t('tenders.link'),
      type: 'text' as const,
      width: 'half' as const
    },
    {
      name: 'date',
      label: t('tenders.date'),
      type: 'date' as const,
      width: 'third' as const
    },
    {
      name: 'docs_price',
      label: t('tenders.docsPrice'),
      type: 'number' as const,
      width: 'third' as const
    },
    {
      name: 'docs_currency',
      label: t('tenders.docsCurrency'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'docs_available_from',
      label: t('tenders.docsAvailableFrom'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'site_visit',
      label: t('tenders.siteVisit'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'inquiries_deadline',
      label: t('tenders.inquiriesDeadline'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'submission_deadline',
      label: t('tenders.submissionDeadline'),
      type: 'date' as const,
      width: 'third' as const
    },
    {
      name: 'submission_time',
      label: t('tenders.submissionTime'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'opening_datetime',
      label: t('tenders.openingDatetime'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'bid_validity_days',
      label: t('tenders.bidValidityDays'),
      type: 'number' as const,
      width: 'third' as const
    },
    {
      name: 'contact_fax',
      label: t('tenders.contactFax'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'website',
      label: t('tenders.website'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'submission_address',
      label: t('tenders.submissionAddress'),
      type: 'text' as const,
      width: 'half' as const
    },
    {
      name: 'source',
      label: t('tenders.source'),
      type: 'text' as const,
      width: 'third' as const
    },
    {
      name: 'is_public',
      label: t('tenders.isPublic'),
      type: 'boolean' as const,
      width: 'third' as const
    },
    {
      name: 'needs_review',
      label: t('tenders.needsReview'),
      type: 'boolean' as const,
      width: 'third' as const
    },
    {
      name: 'ocr_notes',
      label: t('tenders.ocrNotes'),
      type: 'textarea' as const,
      width: 'full' as const
    },
    {
      name: 'attachment',
      label: t('tenders.attachment'),
      type: 'file' as const,
      bucket: 'tender-scans',
      accept: 'application/pdf,image/*',
      width: 'full' as const,
    },
  ];

  return (
    <GenericTablePage
      tableName="tenders"
      title={t('tenders.title')}
      columns={columns}
      fields={fields}
      detailPath={(id) => `/tenders/${id}`}
      editPath={(id) => (id ? `/tenders/${id}/edit` : '/tenders/new')}
    />
  );
};

export default TendersPage;
